import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { MatTabGroup, MatDialog, MatChipInputEvent } from '@angular/material';
import { DomSanitizer } from '@angular/platform-browser';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { MessageService } from 'primeng/primeng';
import { Router } from '@angular/router';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { base64StringToBlob } from 'blob-util';
import { ScreeningService } from '../../services/screening.service';
import { VerificationService } from '../../services/verification.service';
import { CommonService } from '../../services/common.service';
import { AuthService } from '../../services/auth.service';
import { AutoCompleteDropDown } from '../../models/autoComplete';

// Dashboard "Raised Query" details page - a read-only drill-down mirroring InsufficiencyComponent's
// search-bar + tabs + card-list + pagination structure, trimmed to Query's simpler 2-state
// (Raised/Cleared) lifecycle. Clearing a query already happens via the existing RaiseQueryComponent/
// ClearQuery flow on the Verification screening page, so this page has no editing UI.
@Component({
  selector: 'app-raised-query',
  templateUrl: './raised-query.component.html',
  styleUrls: ['./raised-query.component.css'],
  animations: [
    trigger('rotatedState', [
      state('reset', style({ transform: 'rotate(0deg)' })),
      state('right', style({ transform: 'rotate(90deg)' })),
      state('down', style({ transform: 'rotate(180deg)' })),
      state('left', style({ transform: 'rotate(270deg)' })),
      state('up', style({ transform: 'rotate(360deg)' })),
      transition('rotated => default', animate('1500ms ease-out')),
      transition('default => rotated', animate('400ms ease-in'))
    ])
  ]
})
export class RaisedQueryComponent implements OnInit {
  queryFormGroup: FormGroup;
  menubar = [{ menuName: 'Raised' }];
  @ViewChild('tab', null) tab: MatTabGroup;
  CurrentTab = 0;
  queryList: any[] = [];
  levelList: any[] = [];
  shieveTotalCount = 0;
  shievePageNo = 1;
  shievePageSize = 10;
  applyPaging = true;
  userData = JSON.parse(sessionStorage.getItem('user_data'));

  column: any;
  direction: number;
  isDesc: boolean;

  clientNameControl: AutoCompleteDropDown;
  refNoControl: AutoCompleteDropDown;
  verificationIdControl: AutoCompleteDropDown;
  screeningControl: AutoCompleteDropDown;
  candidateNameControl: AutoCompleteDropDown;
  levelControls: AutoCompleteDropDown;

  queryClientName: any[] = [];
  queryClientRefNo: any[] = [];
  queryVerificationId: any[] = [];
  queryCandidateName: any[] = [];
  queryScreeningId: any[] = [];

  queryScreenMode = true;
  selectedQueryDetails: any;
  queryDocuments: any[] = [];
  docDetailTableColumnList = [
    { field: 'queryTypeName', header: 'Query Type' },
    { field: 'queryLevel', header: 'Level' },
    { field: 'responseDocument', header: 'Response Document' },
    { field: 'queryStatus', header: 'Status' },
    { field: 'raisedDate', header: 'Raised Date & Time' },
    { field: 'remarks', header: 'Remarks' },
  ];

  clearedDate = new Date();
  clearRemarks = '';
  clearDocuments: any[] = [];
  maxdate = new Date();
  clearDocTableCol = [
    { field: 'action', header: 'Action' },
    { field: 'fileName', header: 'Document Name' },
  ];

  screenAuth: any = {};

  // Raised Query - "Send To Client": a MatDialog opened from an embedded ng-template
  // (#sendEmailDialog), mirroring screening-details.component.ts's #mailDialog exactly - same
  // fields/behavior, own dedicated template (EmailTempConstants.RaisedQuerySendToClient).
  @ViewChild('sendEmailDialog', { static: true }) sendEmailDialog: TemplateRef<any>;
  // Attached Documents "View" - reuses the exact same viewer (dialogs, controls, animation) as
  // SupportingDocumentsComponent.preview() (screening-details.component.ts's "Attach Documents"
  // section), trimmed to the screeningDocId-only data shape this page's document list ever has.
  @ViewChild('imgprDialog', null) imgprDialog: TemplateRef<any>;
  @ViewChild('pdfDialog', null) pdfDialog: TemplateRef<any>;
  state: string = 'default';
  dir: string;
  imageSource: any;
  url: any;
  downldata: any;
  zoomval: number;
  rvalue: number;
  SendCaseMailList: SendQueryMailVm = new SendQueryMailVm();
  toList: string[] = [];
  ccList: string[] = [];
  selectable = true;
  removable = true;
  addOnBlur = true;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  isMailIdSave = false;
  mailSize: any;
  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '0',
    maxHeight: 'auto',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Enter text here...',
    defaultParagraphSeparator: '',
    defaultFontName: '',
    defaultFontSize: '',
    fonts: [
      { class: 'arial', name: 'Arial' },
      { class: 'times-new-roman', name: 'Times New Roman' },
      { class: 'calibri', name: 'Calibri' },
      { class: 'comic-sans-ms', name: 'Comic Sans MS' }
    ],
    uploadUrl: 'v1/image',
    sanitize: false,
    toolbarPosition: 'top',
  };

  constructor(private route: Router, public common: CommonService, public screeningService: ScreeningService,
    public verificationService: VerificationService, private messageService: MessageService, private auth: AuthService,
    public dialog: MatDialog, private sanitizer: DomSanitizer) { }

  ngOnInit() {
    this.screenAuth = this.auth.getScreenAuth(this.route.url);
    this.initFormGroup();
    this.screeningService.getInsuffDetails(false).subscribe(res => {
      if (res) {
        this.levelList = res.insuffLevel || [];
        this.initAutoCompleteCtrl();
      }
    });
    this.searchQueryList(0, 1);
  }

  initFormGroup() {
    this.queryFormGroup = new FormGroup({
      clientName: new FormControl(),
      clientRefNo: new FormControl(),
      verificationId: new FormControl(),
      screeningId: new FormControl(),
      candidateName: new FormControl(),
      levelId: new FormControl(),
    });
    this.initAutoCompleteCtrl();
  }

  initAutoCompleteCtrl() {
    this.clientNameControl = new AutoCompleteDropDown('Client Name', 'clientName', 'id', 'name',
      this.queryClientName, '', this.queryFormGroup, false, false, false, 'standard');
    this.refNoControl = new AutoCompleteDropDown('Client Reference Number', 'clientRefNo', 'name', 'name',
      this.queryClientRefNo, '', this.queryFormGroup, false, false, false, 'standard');
    this.verificationIdControl = new AutoCompleteDropDown('Verification Id', 'verificationId', 'name', 'name',
      this.queryVerificationId, '', this.queryFormGroup, false, false, false, 'standard');
    this.screeningControl = new AutoCompleteDropDown('Screening Id', 'screeningId', 'name', 'name',
      this.queryScreeningId, '', this.queryFormGroup, false, false, false, 'standard');
    this.candidateNameControl = new AutoCompleteDropDown('Candidate Name', 'candidateName', 'id', 'name',
      this.queryCandidateName, '', this.queryFormGroup, false, false, false, 'standard');
    this.levelControls = new AutoCompleteDropDown('Level', 'levelId', 'lookUpId', 'lookUpName',
      this.levelList, '', this.queryFormGroup, false, false, false, 'standard');
  }

  searchQueryList(tabInd, pageNo) {
    this.shievePageNo = pageNo;
    this.CurrentTab = tabInd;
    this.applyPagination();

    this.screeningService.getQuerySearch(tabInd === 1, this.userData).subscribe(res => {
      if (res) {
        this.shieveTotalCount = res.headers.get('X-Total-Count');
        const respBody = res.body;
        respBody.forEach(m => { m.screeningCompId = 'ACG' + m.screeningCompId; });
        this.queryList = respBody;
        if (this.tab) { this.tab.selectedIndex = tabInd; }

        this.screeningService.getQueryDropdownDetails(tabInd === 1, this.userData).subscribe(dropdownRes => {
          if (dropdownRes) {
            this.queryClientName = dropdownRes.clientName || [];
            this.queryClientRefNo = dropdownRes.clientRefID || [];
            this.queryVerificationId = dropdownRes.verifcationId || [];
            this.queryCandidateName = dropdownRes.candidateName || [];
            this.queryScreeningId = (dropdownRes.clientScreeningId || []).filter(f => f.name !== 'N/A');
            this.initAutoCompleteCtrl();
          }
        });
      }
    });
  }

  applyPagination() {
    let filter = '';
    const form = this.queryFormGroup.value;
    if (form.clientName) {
      filter = (filter ? filter + ',' : '') + 'clientId==' + form.clientName;
    }
    if (form.clientRefNo) {
      filter = (filter ? filter + ',' : '') + 'clientRefNo@=' + form.clientRefNo;
    }
    if (form.verificationId) {
      filter = (filter ? filter + ',' : '') + 'verificationId@=' + form.verificationId;
    }
    if (form.screeningId) {
      filter = (filter ? filter + ',' : '') + 'clientScreeningId==' + form.screeningId;
    }
    if (form.candidateName) {
      filter = (filter ? filter + ',' : '') + 'candidateId==' + form.candidateName;
    }
    if (form.levelId) {
      filter = (filter ? filter + ',' : '') + 'levelLookupId==' + form.levelId;
    }

    this.userData.pageSize = this.shievePageSize;
    this.userData.page = this.shievePageNo;
    this.userData.filters = filter;
    this.userData.sorts = this.direction ? (this.direction === -1 ? '-' + this.column : this.column) : '';
    this.userData.applyPaging = this.applyPaging;
    this.userData.needTotal = true;
  }

  shievePagination(event) {
    this.searchQueryList(this.CurrentTab, event);
  }

  clearQuerySearch() {
    this.queryFormGroup.reset();
    this.searchQueryList(this.CurrentTab, 1);
  }

  sortBy(type) {
    this.isDesc = !this.isDesc;
    this.column = type;
    this.direction = this.isDesc ? 1 : -1;
    this.searchQueryList(this.CurrentTab, 1);
  }

  showall() {
    if (this.shieveTotalCount > 0) {
      this.applyPaging = false;
      this.searchQueryList(this.CurrentTab, 1);
    }
  }

  goBack() {
    this.route.navigate(['dashboard/home']);
  }

  exportAsExcelFile() {
    this.shievePageNo = 1;
    this.applyPagination();
    this.userData.applyPaging = false;

    this.screeningService.getQuerySearch(this.CurrentTab === 1, this.userData).subscribe(res => {
      if (res) {
        const respBody = res.body;
        respBody.forEach(m => { m.screeningCompId = 'ACG' + m.screeningCompId; });
        respBody.forEach((ele, i) => { ele.sno = i + 1; });

        const queryColumn = [
          { field: 'sno', header: 'S No' },
          { field: 'candidateName', header: 'Candidate Name' },
          { field: 'clientName', header: 'Client Name' },
          { field: 'clientRefNo', header: 'Client Reference No' },
          { field: 'screeningCompId', header: 'Verification Id' },
          { field: 'componentName', header: 'Component Name' },
          { field: 'queryTypeName', header: 'Query Type' },
          { field: 'queryLevel', header: 'Level' },
          { field: 'responseDocument', header: 'Response Document' },
          { field: 'queryStatus', header: 'Status' },
          { field: 'queryRaisedBy', header: 'Raised By' },
          { field: 'functionalEntity', header: 'Functional Entity' },
          { field: 'raisedDate', header: 'Raised Date & Time' },
          { field: 'clearedDate', header: 'Cleared Date & Time' },
        ];

        this.common.exportToExcel(queryColumn, respBody, 'Raised Query List', true);
      }
    });
  }

  queryAction(view) {
    this.selectedQueryDetails = view;
    this.queryScreenMode = false;
    this.clearedDate = new Date();
    this.clearRemarks = '';
    this.clearDocuments = [];
    const screeningCompId = this.common.getNuumberFromString(view.screeningCompId);
    this.verificationService.getQueryDocuments(screeningCompId).subscribe(res => {
      this.queryDocuments = res || [];
    });
  }

  // Mirrors SupportingDocumentsComponent.preview()'s screeningDocId branches exactly (same dialogs,
  // same rotate/zoom/download controls) so this page's viewer is identical to Screening Details ->
  // Raise Query -> Attach Documents.
  viewQueryDocument(doc: any) {
    this.pdftool('reset');
    this.rotateimg('reset');
    this.downldata = doc;
    const ext = (doc.fileName || '').split('.').pop();
    if (['png', 'jpg', 'JPG', 'gif', 'jpeg', 'psd', 'bmp'].includes(ext)) {
      this.screeningService.downloadScreeningDocument(doc.screeningDocId).subscribe(resp => {
        if (resp && resp.document) {
          this.imageSource = this.sanitizer.bypassSecurityTrustUrl('data:image/jpg;base64,' + resp.document);
          this.dialog.open(this.imgprDialog, { panelClass: 'myClass', disableClose: true });
        } else {
          this.showTopCenter('warn', 'Alert Message', 'File does not exist');
        }
      });
    } else if (ext === 'pdf' || ext === 'PDF') {
      this.screeningService.downloadScreeningDocument(doc.screeningDocId).subscribe(resp => {
        if (resp && resp.document) {
          const blob = base64StringToBlob(resp.document, 'application/octet-stream');
          this.url = window.URL.createObjectURL(blob);
          this.dialog.open(this.pdfDialog, { panelClass: 'myClass', disableClose: true });
        } else {
          this.showTopCenter('warn', 'Alert Message', 'File does not exist');
        }
      });
    } else {
      this.downloadQueryDocument(doc);
    }
  }

  downloadQueryDocument(doc: any) {
    this.screeningService.downloadScreeningDocument(doc.screeningDocId).subscribe(resp => {
      if (resp && resp.document) {
        this.common.downloadDocument(doc.screeningDocId, resp.document, resp.fileName);
      } else {
        this.showTopCenter('warn', 'Alert Message', 'File does not exist');
      }
    });
  }

  pdftool(type) {
    switch (type) {
      case 'right':
        this.rvalue += 90;
        break;
      case 'left':
        this.rvalue -= 90;
        break;
      case 'zoomin':
        this.zoomval += 0.1;
        break;
      case 'zoomout':
        this.zoomval -= 0.1;
        break;
      case 'reset':
        this.zoomval = 1;
        this.rvalue = 0;
        break;
      case 'download':
        this.downloadQueryDocument(this.downldata);
        break;
      default:
        break;
    }
  }

  zoomin() {
    const myImg = document.getElementById('imgpre');
    const currWidth = myImg.clientWidth;
    if (currWidth === 1500) { return false; }
    myImg.style.width = (currWidth + 100) + 'px';
  }

  zoomout() {
    const myImg = document.getElementById('imgpre');
    const currWidth = myImg.clientWidth;
    if (currWidth === 100) { return false; }
    myImg.style.width = (currWidth - 100) + 'px';
  }

  rotateimg(route) {
    this.dir = route;
    this.state = (this.state === 'default' ? 'rotated' : this.dir);
  }

  openClearUploadDoc(event) {
    const files: FileList = event && event.target && event.target.files;
    if (!files || files.length === 0) { return; }
    const rejected: string[] = [];
    Array.from(files).forEach((file: File) => {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!['png', 'jpg', 'jpeg', 'gif', 'psd', 'bmp'].includes(ext)) {
        rejected.push(file.name);
        return;
      }
      this.clearDocuments.push({ fileName: file.name, document: file });
    });
    if (rejected.length) {
      this.showTopCenter('warn', 'Failure Message',
        'Please upload a valid file, Acceptable file Formats : .png, .jpg, .JPG, .jpeg, .gif, .psd, .bmp'
        + ' (rejected: ' + rejected.join(', ') + ')');
    }
    event.target.value = '';
  }

  removeClearDocument(i) {
    this.clearDocuments.splice(i, 1);
  }

  submitClearQuery() {
    if (!(this.clearRemarks && this.clearRemarks.length > 0)) {
      this.showTopCenter('warn', 'Warning', 'Add Query Remarks');
      return;
    }
    const compId = this.selectedQueryDetails.compId;
    const formData = new FormData();
    this.clearDocuments.forEach((d, i) => {
      formData.append('ScreeningComponentDocument_' + compId + '_' + i, d.document);
    });
    const payload = {
      queryId: this.selectedQueryDetails.queryId,
      screeningCompId: this.common.getNuumberFromString(this.selectedQueryDetails.screeningCompId),
      loggedIn: this.userData.userId,
      clearedDate: this.clearedDate,
      remarks: this.clearRemarks,
      clearDocument: this.clearDocuments.map(d => ({ fileName: d.fileName })),
    };
    formData.append('ClearQuery', JSON.stringify(payload));
    this.verificationService.clearQuery(formData).subscribe(res => {
      if (res && res.success) {
        this.showTopCenter('success', 'Success Message', 'Query cleared successfully');
        this.backToList();
      } else {
        this.showTopCenter('warn', 'Failed to clear', (res && res.message) || 'Unable to clear this query.');
      }
    });
  }

  backToList() {
    this.queryScreenMode = true;
    this.selectedQueryDetails = null;
    this.queryDocuments = [];
    this.clearDocuments = [];
    this.searchQueryList(this.CurrentTab, 1);
  }

  sendToClient() {
    this.dialog.closeAll();
    this.SendCaseMailList = new SendQueryMailVm();
    this.toList = [];
    this.ccList = [];
    this.isMailIdSave = false;
    this.getQueryMailDetails();
    this.dialog.open(this.sendEmailDialog, { width: '1400px', disableClose: true });
  }

  getQueryMailDetails(discloseFlag = false) {
    const screeningCompId = this.common.getNuumberFromString(this.selectedQueryDetails.screeningCompId);
    const queryId = this.selectedQueryDetails.queryId;
    const clientId = this.selectedQueryDetails.clientId;
    this.verificationService.getMailDetails(screeningCompId, 'raisedQuery', clientId, 0, discloseFlag, null, null, queryId).subscribe(dat => {
      if (dat != null) {
        this.SendCaseMailList.EmailTemplate = dat.htmlTemplateBody;
        this.SendCaseMailList.Subject = dat.templateSubject;
        this.SendCaseMailList.TemplateName = dat.templateName;
        this.SendCaseMailList.discloseClientFlag = dat.discloseClientFlag;
        this.SendCaseMailList.discloseEnableFlag = dat.discloseEnableFlag;
        // Only ever populated from this template's own configured recipients (Configure -> Mail
        // Config) - left empty when none are configured, never backfilled from an unrelated
        // default address (matches getVerificationDetails()'s Enquiry/Initiation branch, which
        // never calls getMailId()/Getemployermailid either).
        this.toList = dat.toMail != null ? dat.toMail : [];
        this.ccList = dat.ccMail != null ? dat.ccMail : [];
        this.isMailIdSave = true;
      } else {
        this.showTopCenter('warn', 'Info message', 'Unable to load the email template for this request. Please contact support.');
      }
    });
  }

  openUploadDoc(event) {
    for (let i = 0; i < event.target.files.length; i++) {
      this.mailSize = event.target.files[i].size;
      this.SendCaseMailList.mailDocument.push({
        fileName: event.target.files[i].name,
        document: event.target.files[i]
      });
    }
  }

  addMail(event: MatChipInputEvent, type: 'To' | 'CC') {
    const input = event.input;
    const value = (event.value || '').trim();
    if (!value) { return; }
    if (!this.common.EmailRegX.test(value)) {
      this.showTopCenter('warn', 'Failure Message', 'Please enter valid Email');
    } else if (type === 'To') {
      this.toList.push(value);
    } else {
      this.ccList.push(value);
    }
    if (input) { input.value = ''; }
  }

  removeControl(ind: number, type: 'To' | 'CC') {
    if (type === 'To') {
      this.toList.splice(ind, 1);
    } else {
      this.ccList.splice(ind, 1);
    }
  }

  checkIsExist(type: 'ToInc' | 'ToDec' | 'CCInc' | 'CCDec'): boolean {
    const mailIds = this.SendCaseMailList && this.SendCaseMailList.ClientMailId;
    if (!mailIds || mailIds.length === 0) { return true; }
    if (type === 'ToInc') { return mailIds.filter(e => e.emailType === 'To' && e.selected === false).length === 0; }
    if (type === 'ToDec') { return mailIds.filter(e => e.emailType === 'To' && e.selected === true).length === 0; }
    if (type === 'CCInc') { return mailIds.filter(e => e.emailType === 'CC' && e.selected === false).length === 0; }
    if (type === 'CCDec') { return mailIds.filter(e => e.emailType === 'CC' && e.selected === true).length === 0; }
    return true;
  }

  mailChange(mail: string, assign: boolean) {
    const found = this.SendCaseMailList.ClientMailId.filter(e => e.emailAddress === mail)[0];
    if (found) { found.selected = assign; }
  }

  fileSizeValidation(size): boolean {
    return this.bytesToSize(size);
  }

  bytesToSize(bytes): boolean {
    if (bytes === 0) { return true; }
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    if (i === 2) {
      return Math.round(bytes / Math.pow(1024, i)) <= 5;
    } else if (i > 2) {
      return false;
    }
    return true;
  }

  sendMail() {
    this.SendCaseMailList.LoggedIn = this.userData.userId;
    this.SendCaseMailList.TeamName = this.userData.teamName;
    this.SendCaseMailList.ScreeningCompId = this.common.getNuumberFromString(this.selectedQueryDetails.screeningCompId);

    if (this.toList.length > 0) {
      this.toList.forEach(mail => this.SendCaseMailList.ClientMailId.push({ emailAddress: mail, emailType: 'To', selected: true }));
      this.toList = [];
    }
    if (this.ccList.length > 0) {
      this.ccList.forEach(mail => this.SendCaseMailList.ClientMailId.push({ emailAddress: mail, emailType: 'CC', selected: true }));
      this.ccList = [];
    }

    if (this.SendCaseMailList.ClientMailId.length === 0) {
      this.showTopCenter('warn', 'Info message', 'Please add MailId');
      return;
    }

    const formData = new FormData();
    for (let i = 0; i < this.SendCaseMailList.mailDocument.length; i++) {
      if (this.SendCaseMailList.mailDocument[i].fileName) {
        if (!this.fileSizeValidation(this.mailSize)) {
          this.showTopCenter('warn', 'Failure Message', 'the overall file size should be less than 5 MB');
          return;
        }
        formData.append('ScreeningComponentDocument_0_' + i, this.SendCaseMailList.mailDocument[i].document);
      }
    }
    formData.append('sendverification', JSON.stringify(this.SendCaseMailList));

    this.verificationService.sendVerficationMail(formData).subscribe(res => {
      if (res && res.success) {
        this.showTopCenter('success', 'Success', 'Mail sent Successfully');
        this.dialog.closeAll();
        this.isMailIdSave = false;
      } else {
        this.showTopCenter('warn', 'Info message', (res && res.message) ? res.message : 'Something Went Wrong');
      }
    });
  }

  showTopCenter(level: string, info: string, message: string) {
    this.messageService.add({ severity: level, summary: info, detail: message });
  }
}

class SendQueryMailVm {
  ClientMailId: QueryMailAddress[] = [];
  EmailTemplate: string;
  Subject: string;
  TemplateName: string;
  TeamName: string;
  discloseClientFlag = false;
  discloseEnableFlag = false;
  LoggedIn: number;
  ScreeningCompId: number;
  mailDocument: QueryMailDocument[] = [];
}

class QueryMailDocument {
  fileName: string;
  document: any;
}

class QueryMailAddress {
  emailType: string;
  emailAddress: string;
  selected = false;
}
